<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900">
    <BreadCrumbs />

    <main class="w-full max-w-7xl mx-auto px-6 py-12">
      <!-- Header Section -->
      <div class="mb-8 lg:mb-12">
        <div class="flex flex-col gap-4 lg:gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div class="flex items-center gap-4 mb-3">
              <div class="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <ClipboardDocumentListIcon class="w-5 h-5 lg:w-6 lg:h-6 text-white" />
              </div>
              <div>
                <h1 class="text-2xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900 dark:from-white dark:via-blue-100 dark:to-purple-100 bg-clip-text text-transparent">
                  Research Projects
                </h1>
                <p class="text-base lg:text-lg text-gray-600 dark:text-gray-400 mt-0.5 lg:mt-1">
                  Manage and continue your research projects
                </p>
              </div>
            </div>
            <!-- Stats Row -->
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6 text-sm mt-4">
              <div class="flex items-center gap-2">
                <div class="w-2 h-2 bg-green-500 rounded-full"></div>
                <span class="text-gray-600 dark:text-gray-400">
                  {{ activeProjects?.length || 0 }} active {{ (activeProjects?.length || 0) === 1 ? 'project' : 'projects' }}
                </span>
              </div>
              <div v-if="archivedProjects && archivedProjects.length > 0" class="flex items-center gap-2">
                <div class="w-2 h-2 bg-gray-400 rounded-full"></div>
                <span class="text-gray-500 dark:text-gray-500">
                  {{ archivedProjects.length }} archived
                </span>
              </div>
            </div>
          </div>
          <!-- Actions Section -->
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center mt-4 lg:mt-0">
            <button @click="showNewProjectModal = true"
              class="group relative inline-flex items-center justify-center px-5 py-2.5 lg:px-6 lg:py-3 bg-gradient-to-r from-blue-600 via-blue-700 to-purple-700 text-white rounded-2xl font-semibold shadow-xl shadow-blue-500/25 hover:shadow-2xl hover:shadow-blue-500/40 transform hover:scale-105 transition-all duration-200 overflow-hidden">
              <PlusIcon class="w-4 h-4 lg:w-5 lg:h-5 mr-2 relative z-10" />
              <span class="relative z-10">New Project</span>
            </button>
          </div>
        </div>
        <!-- Toggle for archived and deleted projects -->
        <div class="mt-6 flex flex-col gap-2">
          <label v-if="archivedProjects && archivedProjects.length > 0" class="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300">
            <input type="checkbox" v-model="showArchived" class="form-checkbox rounded text-blue-600 focus:ring-blue-500" />
            Show archived projects ({{ archivedProjects.length }})
          </label>
          <label v-if="deletedProjects && deletedProjects.length > 0" class="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300">
            <input type="checkbox" v-model="showDeleted" class="form-checkbox rounded text-red-600 focus:ring-red-500" />
            Show deleted projects ({{ deletedProjects.length }}) - <span class="text-red-600 text-xs">Restorable for 30 days</span>
          </label>
        </div>

        <!-- Admin Scheduler Controls (uncomment for admin users) -->
        <!-- 
        <div v-if="isAdmin" class="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <h3 class="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">Admin: Deletion Scheduler</h3>
          <div class="flex gap-2">
            <button @click="executeScheduledDeletions" class="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700">
              Execute Scheduled Deletions
            </button>
            <button @click="getSchedulerStatus" class="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">
              Check Status
            </button>
          </div>
        </div>
        -->
      </div>

      <!-- Loading State -->
      <template v-if="projectStore.loading">
        <div class="flex flex-col items-center justify-center py-20">
          <div class="relative mb-6">
            <div class="w-16 h-16 border-4 border-gray-200 dark:border-gray-700 rounded-full"></div>
            <div class="absolute top-0 left-0 w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p class="text-lg text-gray-600 dark:text-gray-400 font-medium">Loading your projects...</p>
          <p class="text-sm text-gray-500 dark:text-gray-500 mt-2">This may take a moment</p>
        </div>
      </template>

      <!-- Error State -->
      <template v-else-if="projectStore.error">
        <div class="text-center py-20">
          <div class="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg class="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-2">Failed to load projects</h3>
          <p class="text-red-600 dark:text-red-400 mb-6">{{ projectStore.error }}</p>
          <button @click="loadProjects"
            class="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium">
            Try Again
          </button>
        </div>
      </template>

      <!-- Projects Grid -->
      <template v-else-if="displayedProjects.length > 0">
        <section>
          <div class="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            <div v-for="project in displayedProjects" :key="project._id"
              class="group relative bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl border border-white/20 dark:border-gray-700/50 rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:transform hover:scale-[1.02]">
              <!-- Status Indicator -->
              <div class="absolute top-4 right-4 flex items-center gap-2">
                <div class="w-3 h-3 rounded-full shadow-sm"
                  :class="project.deleted ? 'bg-red-400' : project.status === 'archived' ? 'bg-gray-400' : 'bg-green-500'"></div>
                <span v-if="project.deleted" class="text-xs font-medium text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30 px-2 py-1 rounded-full">
                  Deleted
                </span>
                <span v-else-if="project.status === 'archived'" class="text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900/30 px-2 py-1 rounded-full">
                  Archived
                </span>
              </div>
              <!-- Project Title -->
              <div class="mb-4">
                <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2 line-clamp-2"
                  :class="{ 'line-through text-gray-500 dark:text-gray-400': project.status === 'archived' || project.deleted }">
                  {{ project.name || 'Unnamed Project' }}
                </h3>
                <div v-if="project.description" class="relative">
                  <div
                    :class="[
                      'inline-flex items-start gap-1.5 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-sm font-medium transition-all',
                      expandedProjectIds.includes(project._id) ? '' : 'line-clamp-3 max-h-20 overflow-hidden'
                    ]"
                  >
                    {{ project.description }}
                  </div>
                  <button
                    v-if="project.description.length > 120"
                    @click="toggleExpand(project._id)"
                    class="absolute right-2 bottom-1 text-xs text-blue-600 hover:underline bg-white/80 dark:bg-gray-900/80 px-1 rounded"
                  >
                    {{ expandedProjectIds.includes(project._id) ? 'Show less' : 'Read more' }}
                  </button>
                </div>
              </div>
              <!-- Info -->
              <div class="space-y-3 mb-6">
                <div class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  {{ formatDate(project.createdAt) }}
                </div>
                <!-- Deletion Info for deleted projects -->
                <div v-if="project.deleted && project.deletedAt" class="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span v-if="project.daysUntilPermanent">
                    {{ project.daysUntilPermanent }} days left to restore
                  </span>
                  <span v-else>
                    Deleted {{ formatDate(project.deletedAt) }}
                  </span>
                </div>
                <!-- Scheduled deletion info -->
                <div v-if="project.scheduledDeletion" class="flex items-center gap-2 text-sm text-orange-600 dark:text-orange-400">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Scheduled for permanent deletion {{ formatDate(project.scheduledDeletion) }}
                </div>
              </div>
              <!-- Actions -->
              <div class="flex gap-3">
                <button @click="selectProject(project._id)"
                  class="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-medium hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-center">
                  Open
                </button>
                <button v-if="!project.deleted" @click="openEditModal(project)"
                  class="px-3 py-2.5 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-xl transition-colors"
                  title="Edit Project">
                  <PencilSquareIcon class="w-5 h-5" />
                </button>
                <button v-if="project.status !== 'archived' && !project.deleted" @click="confirmArchive(project._id)"
                  class="px-3 py-2.5 text-orange-600 hover:bg-orange-100 dark:hover:bg-orange-900/20 rounded-xl transition-colors"
                  title="Archive Project">
                  <ArchiveBoxIcon class="w-5 h-5" />
                </button>
                <button v-if="project.deleted" @click="restoreProject(project._id)"
                  class="px-3 py-2.5 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/20 rounded-xl transition-colors"
                  title="Restore Project">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                <button v-if="project.status === 'archived' && !project.deleted" @click="unarchiveProject(project._id)"
                  class="px-3 py-2.5 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/20 rounded-xl transition-colors"
                  title="Unarchive Project">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                </button>
                <button v-if="project.status === 'archived' || project.deleted" @click="confirmDelete(project._id)"
                  class="px-3 py-2.5 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                  title="Delete Forever">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16">
                    </path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </section>
      </template>

      <!-- Empty State -->
      <template v-else>
        <div class="text-center py-20">
          <div class="relative mb-8">
            <div class="w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/20 dark:to-purple-900/20 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <ClipboardDocumentListIcon class="w-12 h-12 text-blue-600 dark:text-blue-400" />
            </div>
            <div class="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full opacity-20 animate-pulse"></div>
            <div class="absolute -bottom-2 -left-2 w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full opacity-30 animate-pulse" style="animation-delay: 0.5s"></div>
          </div>
          <h3 class="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            No Research Projects
          </h3>
          <p class="text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
            Start your first research project to see it here.
          </p>
          <button @click="showNewProjectModal = true"
            class="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 via-blue-700 to-purple-700 text-white rounded-2xl font-semibold shadow-xl shadow-blue-500/25 hover:shadow-2xl hover:shadow-blue-500/40 transform hover:scale-105 transition-all duration-300">
            <PlusIcon class="w-6 h-6 mr-3 group-hover:rotate-90 transition-transform duration-300" />
            Start Your First Research Project
          </button>
        </div>
      </template>
    </main>

    <!-- New Project Modal -->
    <template v-if="showNewProjectModal">
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div class="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 w-full max-w-md mx-auto">
          <h2 class="text-xl font-bold mb-4 text-gray-900 dark:text-white">Create New Project</h2>
          <form @submit.prevent="handleCreateProject">
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Project Name</label>
              <input v-model="newProjectName" type="text" required
                class="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div class="mb-6">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <textarea v-model="newProjectDescription" rows="3"
                class="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"></textarea>
            </div>
            <div class="flex gap-3 justify-end">
              <button type="button" @click="showNewProjectModal = false"
                class="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition">
                Cancel
              </button>
              <button type="submit"
                class="px-6 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-700 text-white font-semibold shadow hover:from-blue-700 hover:to-purple-800 transition">
                Create
              </button>
            </div>
          </form>
        </div>
      </div>
    </template>

    <!-- Edit Project Modal -->
    <template v-if="showEditProjectModal">
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div class="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 w-full max-w-md mx-auto">
          <h2 class="text-xl font-bold mb-4 text-gray-900 dark:text-white">Edit Project</h2>
          <form @submit.prevent="handleEditProject">
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Project Name</label>
              <input v-model="editProjectName" type="text" required
                class="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div class="mb-6">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <textarea v-model="editProjectDescription" rows="3"
                class="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"></textarea>
            </div>
            <div class="flex gap-3 justify-end">
              <button type="button" @click="showEditProjectModal = false"
                class="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition">
                Cancel
              </button>
              <button type="submit"
                class="px-6 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-700 text-white font-semibold shadow hover:from-blue-700 hover:to-purple-800 transition">
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </template>

    <!-- Archive Confirmation Modal -->
    <template v-if="showArchiveModal">
      <div :key="archiveProjectId" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div class="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 w-full max-w-md mx-auto">
          <h2 class="text-xl font-bold mb-4 text-gray-900 dark:text-white">Archive Project</h2>
          <p class="mb-6 text-gray-700 dark:text-gray-300">
            Are you sure you want to archive this project? You can restore it later, but it will be hidden from your active projects.
          </p>
          <div class="flex gap-3 justify-end">
            <button type="button" @click="showArchiveModal = false"
              class="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition">
              Cancel
            </button>
            <button type="button" @click="archiveProject(archiveProjectId)"
              class="px-6 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-orange-700 text-white font-semibold shadow hover:from-orange-600 hover:to-orange-800 transition">
              Archive
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- Enhanced Delete Modal -->
    <template v-if="showDeleteModal">
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div class="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 w-full max-w-lg mx-auto">
          <h2 class="text-xl font-bold mb-4 text-gray-900 dark:text-white">Delete Project</h2>
          
          <!-- Deletion Type Selection -->
          <div class="mb-6 space-y-4">
            <p class="text-gray-700 dark:text-gray-300 mb-4">Choose how you want to delete this project:</p>
            
            <!-- Soft Delete (Default) -->
            <label class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                   :class="deleteType === 'soft' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700'">
              <input type="radio" v-model="deleteType" value="soft" class="mt-1 text-blue-600" />
              <div>
                <div class="font-medium text-gray-900 dark:text-white">Archive (Recommended)</div>
                <div class="text-sm text-gray-600 dark:text-gray-400">Hide project but keep data. Can be restored within 30 days.</div>
              </div>
            </label>
            
            <!-- Scheduled Delete -->
            <label class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                   :class="deleteType === 'scheduled' ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' : 'border-gray-200 dark:border-gray-700'">
              <input type="radio" v-model="deleteType" value="scheduled" class="mt-1 text-orange-600" />
              <div class="flex-1">
                <div class="font-medium text-gray-900 dark:text-white">Schedule Permanent Deletion</div>
                <div class="text-sm text-gray-600 dark:text-gray-400 mb-2">Archive now, permanently delete after retention period.</div>
                <div v-if="deleteType === 'scheduled'" class="mt-2">
                  <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Retention Days:</label>
                  <input type="number" v-model.number="retentionDays" min="1" max="365" 
                         class="w-20 px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
                </div>
              </div>
            </label>
            
            <!-- Hard Delete -->
            <label class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                   :class="deleteType === 'hard' ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-gray-700'">
              <input type="radio" v-model="deleteType" value="hard" class="mt-1 text-red-600" />
              <div>
                <div class="font-medium text-red-700 dark:text-red-400">Permanent Delete</div>
                <div class="text-sm text-red-600 dark:text-red-400">⚠️ Immediately delete all data. Cannot be undone!</div>
              </div>
            </label>
          </div>
          
          <!-- Warning for hard delete -->
          <div v-if="deleteType === 'hard'" class="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg">
            <div class="flex items-center gap-2 text-red-800 dark:text-red-400 text-sm font-medium">
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
              This will permanently delete all research data, sessions, articles, and AI embeddings.
            </div>
          </div>
          
          <div class="flex gap-3 justify-end">
            <button type="button" @click="showDeleteModal = false"
              class="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition">
              Cancel
            </button>
            <button type="button" @click="deleteProject(deleteProjectId)"
              :class="[
                'px-6 py-2 rounded-lg font-semibold shadow transition',
                deleteType === 'hard' 
                  ? 'bg-gradient-to-r from-red-600 to-red-800 text-white hover:from-red-700 hover:to-red-900'
                  : deleteType === 'scheduled'
                  ? 'bg-gradient-to-r from-orange-600 to-orange-800 text-white hover:from-orange-700 hover:to-orange-900'
                  : 'bg-gradient-to-r from-blue-600 to-blue-800 text-white hover:from-blue-700 hover:to-blue-900'
              ]">
              {{ deleteType === 'soft' ? 'Archive' : deleteType === 'scheduled' ? 'Schedule Delete' : 'Permanently Delete' }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { onMounted, ref, computed, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useProjectStore } from '@/stores/project'
import { ClipboardDocumentListIcon, PlusIcon, PencilSquareIcon, ArchiveBoxIcon } from '@heroicons/vue/24/outline'
import BreadCrumbs from '@/layouts/BreadCrumbs.vue'

const router = useRouter()
const projectStore = useProjectStore()

const showNewProjectModal = ref(false)
const newProjectName = ref('')
const newProjectDescription = ref('')
const showArchived = ref(false)
const showDeleted = ref(false)
const deletedProjects = ref([])
const showEditProjectModal = ref(false)
const editProjectName = ref('')
const editProjectDescription = ref('')
const editProjectId = ref(null)

// Archive/Delete modal state
const showArchiveModal = ref(false)
const archiveProjectId = ref(null)
const showDeleteModal = ref(false)
const deleteProjectId = ref(null)
const deleteType = ref('soft')
const retentionDays = ref(30)
const deleteResult = ref(null)



// Load projects on mount
onMounted(() => {
  loadProjects()
  loadDeletedProjects()
})

async function loadDeletedProjects() {
  try {
    const result = await projectStore.fetchDeletedProjects()
    deletedProjects.value = result || []
  } catch (error) {
    console.error('Failed to load deleted projects:', error)
    deletedProjects.value = []
  }
}

async function loadProjects() {
  await projectStore.fetchProjects()
}

// Filtered projects
const activeProjects = computed(() => {
  const projects = projectStore.projects || []
  return projects.filter(p => p.status !== 'archived' && !p.deleted)
})

const archivedProjects = computed(() => {
  const projects = projectStore.projects || []
  return projects.filter(p => p.status === 'archived' || p.deleted)
})

const displayedProjects = computed(() => {
  let projects = [...activeProjects.value]
  
  if (showArchived.value) {
    projects = [...projects, ...archivedProjects.value]
  }
  
  if (showDeleted.value) {
    const deleted = deletedProjects.value || []
    projects = [...projects, ...deleted]
  }
  
  return projects
})

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

async function handleCreateProject() {
  if (!newProjectName.value.trim()) return
  await projectStore.createProject({
    name: newProjectName.value,
    description: newProjectDescription.value
  })
  showNewProjectModal.value = false
  newProjectName.value = ''
  newProjectDescription.value = ''
  await loadProjects()
}

function selectProject(projectId) {
  projectStore.selectProject(projectId)
  router.push({ name: 'Research', query: { projectId } })
  // If your router uses path params, use:
  // router.push({ name: 'Research', params: { projectId } })
}

function confirmArchive(projectId) {
  archiveProjectId.value = projectId
  showArchiveModal.value = true
}

async function archiveProject(projectId) {
  try {
    await projectStore.updateProject(projectId, { status: 'archived' })
    showArchiveModal.value = false
    archiveProjectId.value = null
    await nextTick() // Force DOM update
    await loadProjects()
  } catch (error) {
    console.error('Failed to archive project:', error)
    showArchiveModal.value = false // Close modal even if there's an error
  }
}

function confirmDelete(projectId) {
  deleteProjectId.value = projectId
  deleteType.value = 'soft' // Reset to default
  retentionDays.value = 30  // Reset to default
  showDeleteModal.value = true
}

async function deleteProject(projectId) {
  try {
    const options = { deleteType: deleteType.value }
    if (deleteType.value === 'scheduled') {
      options.retentionDays = retentionDays.value
    }
    
    const result = await projectStore.deleteProject(projectId, options)
    deleteResult.value = result
    
    // Show success message with statistics
    showDeleteSuccessMessage(result)
    
    showDeleteModal.value = false
    deleteProjectId.value = null
    
    await loadProjects()
    await loadDeletedProjects() // Refresh deleted projects list
  } catch (error) {
    console.error('Failed to delete project:', error)
  }
}

function showDeleteSuccessMessage(result) {
  if (result?.dataStats) {
    const { dataStats, message, deletionType } = result
    console.log(`${message}`)
    console.log(`Data removed: ${dataStats.totalRecords} records (${dataStats.estimatedSizeMB}MB)`)
    console.log(`- Sessions: ${dataStats.sessions}`)
    console.log(`- Articles: ${dataStats.articles}`)
    console.log(`- Entity Records: ${dataStats.entityRecords}`)
    console.log(`- Vector Embeddings: ${dataStats.vectorEmbeddings}`)
  }
}

async function restoreProject(projectId) {
  try {
    const result = await projectStore.restoreProject(projectId)
    await loadProjects()
    await loadDeletedProjects() // Refresh deleted projects list
    
    console.log('Project restored successfully:', result.message)
    if (result.daysSinceDeleted) {
      console.log(`Project was deleted ${result.daysSinceDeleted} days ago`)
    }
  } catch (error) {
    console.error('Failed to restore project:', error)
  }
}

async function unarchiveProject(projectId) {
  try {
    await projectStore.updateProject(projectId, { status: 'active' })
    await loadProjects()
    console.log('Project unarchived successfully')
  } catch (error) {
    console.error('Failed to unarchive project:', error)
  }
}

const expandedProjectIds = ref([])
function toggleExpand(projectId) {
  if (expandedProjectIds.value.includes(projectId)) {
    expandedProjectIds.value = expandedProjectIds.value.filter(id => id !== projectId)
  } else {
    expandedProjectIds.value.push(projectId)
  }
}

// Edit project modal
function openEditModal(project) {
  editProjectId.value = project._id
  editProjectName.value = project.name || ''
  editProjectDescription.value = project.description || ''
  showEditProjectModal.value = true
}

async function handleEditProject() {
  if (!editProjectId.value) {
    console.error('No project ID set for editing')
    return
  }
  
  try {
    await projectStore.updateProject(editProjectId.value, {
      name: editProjectName.value,
      description: editProjectDescription.value
    })
    
    // Reset modal state
    showEditProjectModal.value = false
    editProjectId.value = null
    editProjectName.value = ''
    editProjectDescription.value = ''
    
    // Reload projects to show updated data
    await loadProjects()
  } catch (error) {
    console.error('Failed to update project:', error)
    // You might want to show an error message to the user here
  }
}
</script>